import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import type { ComboboxOption } from '../../components/Combobox';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { MultiCombobox } from '../../components/MultiCombobox';
import { SkeletonText } from '../../components/Skeleton';
import { useSession } from '../auth/queries';
import { useAssignableUsers } from '../users/queries';
import type { Appointment } from './api';
import { addDays, berlinToInstant, dayOf, timeOf, today } from './dates';
import { formatRange } from './presentation';
import {
  useAppointments,
  useCreateAppointment,
  useDeleteAppointment,
  useUpdateAppointment,
} from './queries';

/**
 * Die Termine eines Tickets -- F1-51, K-53.
 *
 * **Ein Tag, zwei Uhrzeiten.** Ein Termin am Ticket ist ein Einsatz von ein
 * paar Stunden; ein zweites Datumsfeld fuer das Ende waere bei neunundneunzig
 * von hundert Terminen dasselbe Datum noch einmal. Die Schnittstelle kennt
 * mehrtaegige Termine -- die Maske nicht.
 *
 * **Die Einladung ist ein Haken und keine Adresse.** Sie geht an die Adresse
 * aus dem Profil jedes eingetragenen Mitarbeiters (F1-52). Ein Feld fuer eine
 * freie Adresse gibt es bewusst nicht: Die Einladung traegt Kunde und
 * Ticketinhalt, und die sollen nicht an eine Adresse gehen, die jemand beim
 * Anlegen vertippt.
 */
export function AppointmentPanel({ ticketId, canWrite }: { ticketId: string; canWrite: boolean }) {
  const { t, i18n } = useTranslation();
  const appointments = useAppointments({ ticket_id: ticketId });
  const remove = useDeleteAppointment();

  // `null`: keine Maske offen. `'new'`: Neuanlage. Sonst die Kennung des
  // Termins, der bearbeitet wird.
  const [editing, setEditing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const list = appointments.data ?? [];

  return (
    <section className="card" aria-label={t('appointments.title')}>
      <div className="row">
        <h2>{t('appointments.title')}</h2>
        {list.length > 0 ? <Badge tone="info">{String(list.length)}</Badge> : null}
      </div>

      {appointments.isPending ? <SkeletonText lines={3} /> : null}
      {appointments.isError ? <ErrorNotice error={appointments.error} /> : null}

      {appointments.isSuccess && list.length === 0 && editing !== 'new' ? (
        <EmptyState icon="calendar" title={t('appointments.empty')} />
      ) : null}

      {list.length > 0 ? (
        <ul className="appointment-list">
          {list.map((appointment) =>
            editing === appointment.id ? (
              <li key={appointment.id}>
                <AppointmentForm
                  ticketId={ticketId}
                  initial={appointment}
                  onDone={() => {
                    setEditing(null);
                  }}
                />
              </li>
            ) : (
              <li key={appointment.id} className="appointment">
                <span className="appointment__when">
                  {formatRange(appointment.starts_at, appointment.ends_at, i18n.language)}
                </span>
                {appointment.location ? (
                  <span className="appointment__meta">
                    {t('appointments.location')}: {appointment.location}
                  </span>
                ) : null}
                {appointment.description ? (
                  <p className="appointment__description">{appointment.description}</p>
                ) : null}
                <span className="appointment__meta">
                  {appointment.participants.map((participant) => (
                    <Badge key={participant.user_id} tone="neutral">
                      {participant.full_name}
                      {participant.invited_at ? ` · ${t('appointments.invited')}` : ''}
                    </Badge>
                  ))}
                </span>
                {canWrite ? (
                  <span className="row">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditing(appointment.id);
                        setConfirming(null);
                      }}
                    >
                      {t('appointments.edit')}
                    </Button>
                    {confirming === appointment.id ? (
                      <>
                        <span className="field__hint" role="alert">
                          {t('appointments.confirmDelete')}
                        </span>
                        <Button
                          variant="danger"
                          busy={remove.isPending}
                          onClick={() => {
                            remove.mutate(appointment.id, {
                              onSuccess: () => {
                                setConfirming(null);
                              },
                            });
                          }}
                        >
                          {t('appointments.confirmDeleteYes')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setConfirming(null);
                          }}
                        >
                          {t('app.cancel')}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setConfirming(appointment.id);
                        }}
                      >
                        {t('appointments.delete')}
                      </Button>
                    )}
                  </span>
                ) : null}
              </li>
            ),
          )}
        </ul>
      ) : null}

      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      {canWrite && editing === 'new' ? (
        <AppointmentForm
          ticketId={ticketId}
          onDone={() => {
            setEditing(null);
          }}
        />
      ) : null}

      {canWrite && editing === null ? (
        <Button
          variant="primary"
          onClick={() => {
            setEditing('new');
          }}
        >
          {t('appointments.add')}
        </Button>
      ) : null}
    </section>
  );
}

/** Die naechste volle Stunde in Berlin -- der Vorschlag fuer einen neuen Termin. */
function nextFullHour(): { day: string; time: string } {
  const now = new Date();
  const hour = Number(timeOf(now).slice(0, 2)) + 1;
  if (hour >= 23) return { day: addDays(today(), 1), time: '09:00' };
  return { day: dayOf(now), time: `${String(hour).padStart(2, '0')}:00` };
}

function plusOneHour(time: string): string {
  const hour = Math.min(Number(time.slice(0, 2)) + 1, 23);
  return `${String(hour).padStart(2, '0')}:${time.slice(3, 5)}`;
}

function AppointmentForm({
  ticketId,
  initial,
  onDone,
}: {
  ticketId: string;
  initial?: Appointment;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const session = useSession();
  const users = useAssignableUsers();
  const create = useCreateAppointment();
  const update = useUpdateAppointment();

  const suggestion = nextFullHour();
  const [date, setDate] = useState(initial ? dayOf(initial.starts_at) : suggestion.day);
  const [start, setStart] = useState(initial ? timeOf(initial.starts_at) : suggestion.time);
  const [end, setEnd] = useState(initial ? timeOf(initial.ends_at) : plusOneHour(suggestion.time));
  const [location, setLocation] = useState(initial?.location ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  // `null` heisst: noch nicht angefasst. Dann gilt der Anmeldende als
  // Vorbelegung -- abgeleitet und nicht einmalig gesetzt, denn die Sitzung
  // kann beim Oeffnen der Maske noch unterwegs sein, und eine einmal leer
  // uebernommene Vorbelegung fuellte sich nie mehr.
  const [picked, setPicked] = useState<string[] | null>(
    initial ? initial.participants.map((participant) => participant.user_id) : null,
  );
  const ownId = session.data?.user_id;
  const participants = picked ?? (ownId ? [ownId] : []);
  const [sendInvitation, setSendInvitation] = useState(initial?.send_invitation ?? false);

  // Nur Mitarbeiter. Die Liste der zuweisbaren Nutzer fuehrt auch
  // Portalzugaenge -- der Server wiese sie ab, und eine Auswahl, die in eine
  // Fehlermeldung fuehrt, gehoert nicht in die Maske.
  const options: ComboboxOption[] = (users.data ?? [])
    .filter((user) => user.user_type === 'agent' && user.is_active)
    .map((user) => ({ id: user.id, label: user.full_name }));

  const endBeforeStart = end <= start;
  const noParticipants = participants.length === 0;
  const mutation = initial ? update : create;
  const prefix = initial ? `appointment-${initial.id}` : 'appointment-new';

  const submit = () => {
    if (endBeforeStart || noParticipants) return;
    const values = {
      starts_at: berlinToInstant(date, start).toISOString(),
      ends_at: berlinToInstant(date, end).toISOString(),
      location: location.trim() === '' ? null : location.trim(),
      description: description.trim(),
      participant_ids: participants,
      send_invitation: sendInvitation,
    };
    if (initial) {
      update.mutate({ id: initial.id, payload: values }, { onSuccess: onDone });
    } else {
      create.mutate({ ticket_id: ticketId, ...values }, { onSuccess: onDone });
    }
  };

  return (
    <div className="stack appointment">
      <h3>{initial ? t('appointments.editTitle') : t('appointments.newTitle')}</h3>

      <div className="row">
        <div className="field">
          <label className="field__label" htmlFor={`${prefix}-date`}>
            {t('appointments.date')}
          </label>
          <input
            id={`${prefix}-date`}
            type="date"
            className="field__control"
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
            }}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor={`${prefix}-start`}>
            {t('appointments.start')}
          </label>
          <input
            id={`${prefix}-start`}
            type="time"
            className="field__control"
            value={start}
            onChange={(event) => {
              setStart(event.target.value);
            }}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor={`${prefix}-end`}>
            {t('appointments.end')}
          </label>
          <input
            id={`${prefix}-end`}
            type="time"
            className="field__control"
            value={end}
            aria-invalid={endBeforeStart}
            onChange={(event) => {
              setEnd(event.target.value);
            }}
          />
        </div>
      </div>
      {endBeforeStart ? (
        <span className="field__error" role="alert">
          {t('appointments.endBeforeStart')}
        </span>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor={`${prefix}-location`}>
          {t('appointments.locationOptional')}
        </label>
        <input
          id={`${prefix}-location`}
          className="field__control"
          value={location}
          maxLength={300}
          placeholder={t('appointments.locationPlaceholder')}
          onChange={(event) => {
            setLocation(event.target.value);
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor={`${prefix}-description`}>
          {t('appointments.description')}
        </label>
        <textarea
          id={`${prefix}-description`}
          className="field__control"
          rows={3}
          maxLength={2000}
          value={description}
          placeholder={t('appointments.descriptionPlaceholder')}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor={`${prefix}-participants`}>
          {t('appointments.participants')}
        </label>
        <MultiCombobox
          id={`${prefix}-participants`}
          ariaLabel={t('appointments.participants')}
          options={options}
          value={participants}
          onChange={setPicked}
          placeholder={t('appointments.participantsPlaceholder')}
        />
        <span className={noParticipants ? 'field__error' : 'field__hint'}>
          {noParticipants ? t('appointments.noParticipants') : t('appointments.participantsHint')}
        </span>
      </div>

      {/* Die Einladung -- F1-52. Der Hinweis nennt, was darin steht: Kunde
          und Ticketinhalt gehen damit in das Postfach und den Kalender des
          Mitarbeiters, und das soll niemand erst nach dem Absenden erfahren. */}
      <div className="field">
        <label className="field__checkbox">
          <input
            id={`${prefix}-invite`}
            type="checkbox"
            checked={sendInvitation}
            onChange={(event) => {
              setSendInvitation(event.target.checked);
            }}
          />
          {t('appointments.sendInvitation')}
        </label>
        <span className="field__hint">{t('appointments.sendInvitationHint')}</span>
        {initial ? (
          <span className="field__hint">{t('appointments.invitationKeptHint')}</span>
        ) : null}
      </div>

      {mutation.isError ? <ErrorNotice error={mutation.error} /> : null}

      <div className="row">
        <Button
          variant="primary"
          busy={mutation.isPending}
          disabled={endBeforeStart || noParticipants}
          onClick={submit}
        >
          {t('app.save')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('app.cancel')}
        </Button>
      </div>
    </div>
  );
}
